import { useId, type CSSProperties } from 'react'

/**
 * Corsalia — Reputation rank icons (art fourni, 4 factions × 5 rangs gagnés).
 * The visual tiers are 0-indexed earned ranks (0 Novice … 4 Légende), plus a
 * neutral "Sans pavillon". The PUBLIC `rank` prop is the BACKEND notoriety tier
 * (Reputation::rank(), 0..5 where 0 = neutral, 1 Novice … 5 Légende) — it is
 * mapped internally to the 0..4 earned index, so callers pass `rep.rank` directly.
 *
 * Drop-in for ReputationCard: <ReputationIcon faction={rep.faction} rank={rep.rank} />
 *
 * Each higher rank: more glyph detail · richer medallion frame
 * (ring → laurel → crown) · +1 rank star · colour shifts gold → faction ·
 * gilded gradient at Légende.
 */

export type RepFaction = 'guilde' | 'marine' | 'corsaire' | 'pirate'
export type RepRank = 0 | 1 | 2 | 3 | 4

type Swatch = { line: string; deep: string; hi: string }

const PALETTE: Record<RepFaction | 'gold', Swatch> = {
  gold:     { line: '#E5CC8F', deep: '#C9A55C', hi: '#F5EDD7' },
  guilde:   { line: '#E5CC8F', deep: '#C9A55C', hi: '#F5EDD7' },
  marine:   { line: '#6FC6D8', deep: '#1E96B5', hi: '#BFE4ED' },
  corsaire: { line: '#D6A85E', deep: '#9A6B3A', hi: '#F0D7A0' },
  pirate:   { line: '#E0664B', deep: '#7A3E1F', hi: '#F0A18C' },
}

export const RANK_NAMES = ['Novice', 'Confirmé', 'Notoire', 'Maître', 'Légende'] as const

const glyphColor = (f: RepFaction, r: number) =>
  r <= 1 ? PALETTE.gold.deep : r === 2 ? PALETTE.gold.line : PALETTE[f].line
const glyphSW = (r: number) => [3.1, 3.0, 2.9, 2.8, 2.7][r]
const glyphOpacity = (r: number) => (r === 0 ? 0.62 : 1)
const frameColor = (f: RepFaction, r: number) => (r <= 2 ? PALETTE.gold.deep : PALETTE[f].line)

const star = (cx: number, cy: number, r: number, fill: string) =>
  `<path d="M${cx} ${cy - r} L${cx + r * 0.32} ${cy - r * 0.32} L${cx + r} ${cy - r * 0.28} L${cx + r * 0.45} ${cy + r * 0.18} L${cx + r * 0.6} ${cy + r} L${cx} ${cy + r * 0.5} L${cx - r * 0.6} ${cy + r} L${cx - r * 0.45} ${cy + r * 0.18} L${cx - r} ${cy - r * 0.28} L${cx - r * 0.32} ${cy - r * 0.32} Z" fill="${fill}" stroke="none"/>`

function rankStars(rank: number, faction: RepFaction) {
  const n = rank + 1
  const col = rank >= 3 ? PALETTE[faction].hi : PALETTE.gold.line
  const dim = rank === 0 ? PALETTE.gold.deep : col
  let out = ''
  for (let i = 0; i < n; i++) out += star(60 + (i - (n - 1) / 2) * 9.5, 104, 3.1, dim)
  return `<g opacity="${rank === 0 ? 0.7 : 1}">${out}</g>`
}

function frame(rank: number, faction: RepFaction) {
  const c = frameColor(faction, rank)
  let s = ''
  if (rank >= 1)
    s += `<circle cx="60" cy="60" r="53" fill="none" stroke="${c}" stroke-width="${rank >= 4 ? 2.6 : 1.8}" opacity="${rank >= 3 ? 0.9 : 0.55}"/>`
  if (rank >= 2) {
    s += `<circle cx="60" cy="60" r="47" fill="none" stroke="${c}" stroke-width="1" opacity="0.4"/>`
    s += `<g stroke="${c}" stroke-width="1.4" opacity="0.6"><path d="M60 6 v5"/><path d="M60 114 v-5"/><path d="M6 60 h5"/><path d="M114 60 h-5"/></g>`
  }
  if (rank >= 3) {
    const leaf = (mx: number, sign: number) =>
      `<path d="M${mx} 102 q${sign * -7} -3 ${sign * -9} -8"/><path d="M${mx + sign * -3} 96 q${sign * -7} -2 ${sign * -9} -7"/><path d="M${mx + sign * -6} 90 q${sign * -7} -2 ${sign * -8} -7"/><path d="M${mx + sign * -8} 84 q${sign * -6} -2 ${sign * -7} -6"/>`
    s += `<g fill="none" stroke="${PALETTE[faction].deep}" stroke-width="2" stroke-linecap="round" opacity="0.9"><path d="M52 106 q-14 -2 -22 -14 q-6 -10 -5 -22"/><path d="M68 106 q14 -2 22 -14 q6 -10 5 -22"/>${leaf(48, 1)}${leaf(72, -1)}</g>`
  }
  if (rank >= 4) {
    s += `<g fill="none" stroke="${PALETTE[faction].hi}" stroke-width="2.2" stroke-linejoin="round"><path d="M44 16 L48 7 L54 13 L60 4 L66 13 L72 7 L76 16 Z" fill="${PALETTE[faction].deep}"/></g>${star(48, 7, 1.6, PALETTE.gold.hi)}${star(60, 4, 1.9, PALETTE.gold.hi)}${star(72, 7, 1.6, PALETTE.gold.hi)}`
  }
  return s
}

function guildeGlyph(rank: number, fill: string) {
  let s = `<path d="M38 44 H82"/><path d="M60 36 V78"/><path d="M48 80 H72"/><path d="M54 80 L60 73 L66 80"/><path d="M38 44 L32 56"/><path d="M38 44 L44 56"/><path d="M30 56 Q38 65 46 56"/><path d="M82 44 L76 56"/><path d="M82 44 L88 56"/><path d="M74 56 Q82 65 90 56"/><circle cx="60" cy="34" r="2.3" fill="${fill}" stroke="none"/>`
  if (rank >= 1) s += `<circle cx="60" cy="44" r="2.4"/><circle cx="38" cy="44" r="1.6"/><circle cx="82" cy="44" r="1.6"/>`
  if (rank >= 2) s += `<circle cx="38" cy="56" r="3"/><circle cx="82" cy="56" r="3"/>`
  if (rank >= 3) s += `<path d="M30 56 Q38 66 46 56"/><path d="M74 56 Q82 66 90 56"/><path d="M38 41 V44"/><path d="M82 41 V44"/><path d="M48 80 Q44 84 48 88"/><path d="M72 80 Q76 84 72 88"/>`
  if (rank >= 4) s += `<path d="M60 24 L64 30 L60 36 L56 30 Z"/><circle cx="38" cy="51.5" r="2.4"/><circle cx="82" cy="51.5" r="2.4"/><circle cx="60" cy="44" r="4"/><circle cx="60" cy="30" r="1" fill="${fill}" stroke="none"/>`
  return s
}

function marineGlyph(rank: number, fill: string) {
  let s = `<circle cx="60" cy="33" r="5"/><path d="M60 38 V76"/><path d="M47 47 H73"/><path d="M42 60 Q42 78 60 79 Q78 78 78 60"/><path d="M42 60 L36 63"/><path d="M42 60 L44 53"/><path d="M78 60 L84 63"/><path d="M78 60 L76 53"/>`
  if (rank >= 1) s += `<circle cx="60" cy="33" r="1.9" fill="${fill}" stroke="none"/><path d="M47 47 L45 44"/><path d="M73 47 L75 44"/>`
  if (rank >= 2) s += `<path d="M55 49 H65"/><path d="M55 54 H65"/><path d="M36 63 L33 60"/><path d="M84 63 L87 60"/>`
  if (rank >= 3) s += `<path d="M49 51 H71"/><path d="M42 60 L38 57"/><path d="M78 60 L82 57"/>`
  // rank 4 (Légende) keeps the clean rank-3 anchor; prestige is in the frame.
  return s
}

function corsaireGlyph(rank: number, fill: string) {
  let s = `<path d="M54 62 L54 41 L60 32 L66 41 L66 62"/><path d="M60 36 V60"/><path d="M46 63 H74"/><path d="M46 63 Q43 58 47 57"/><path d="M74 63 Q77 58 73 57"/><path d="M60 63 V80"/><path d="M56 67 H64"/><path d="M56 71 H64"/><circle cx="60" cy="83" r="2.6"/>`
  if (rank >= 1) s += `<circle cx="60" cy="63" r="2" fill="${fill}" stroke="none"/><path d="M56 75 H64"/>`
  if (rank >= 2) s += `<circle cx="60" cy="84" r="4"/>${star(60, 84, 2, fill)}<path d="M57 87 L54 93"/><path d="M63 87 L66 93"/>`
  if (rank >= 3) s += `<path d="M37 78 L83 42" opacity="0.85"/><path d="M83 42 L78 43 M83 42 L81 47"/><path d="M37 78 L41 79 M37 78 L39 74"/>`
  if (rank >= 4) s += `<path d="M83 78 L37 42" opacity="0.85"/><path d="M37 42 L42 43 M37 42 L39 47"/><path d="M83 78 L79 79 M83 78 L81 74"/><path d="M57 41 L60 35 L63 41"/>`
  return s
}

function pirateGlyph(rank: number, fill: string) {
  const SKULL = 'M60 20 C73 20 81 29 81 44 C81 51 78 55 74 58 C74 63 73 67 72 70 C71 74 67 77 60 77 C53 77 49 74 48 70 C47 67 46 63 46 58 C42 55 39 51 39 44 C39 29 47 20 60 20 Z'
  let s = `<path d="${SKULL}"/><path d="M50 70 Q60 75 70 70" fill="none"/><ellipse cx="51" cy="45" rx="6.5" ry="7"/><ellipse cx="69" cy="45" rx="6.5" ry="7"/><path d="M60 53 C57.5 56 56 59 57 61 C58 62.5 62 62.5 63 61 C64 59 62.5 56 60 53 Z"/><path d="M54 70 V77"/><path d="M60 70 V77.5"/><path d="M66 70 V77"/>`
  if (rank >= 1) s += `<path d="M44 52 Q48 57 53 58"/><path d="M76 52 Q72 57 67 58"/><path d="M57 70 V76.5"/><path d="M63 70 V76.5"/><ellipse cx="51" cy="45" rx="4" ry="4.4"/><ellipse cx="69" cy="45" rx="4" ry="4.4"/>`
  if (rank >= 2) s += `<path d="M31 92 L89 72" opacity="0.85"/><path d="M89 92 L31 72" opacity="0.85"/><circle cx="31" cy="92" r="2.2"/><circle cx="89" cy="72" r="2.2"/><circle cx="89" cy="92" r="2.2"/><circle cx="31" cy="72" r="2.2"/>`
  if (rank >= 3) s += `<path d="M44 38 Q51 35 58 39"/><path d="M76 38 Q69 35 62 39"/><ellipse cx="51" cy="45" rx="2" ry="2.3"/><ellipse cx="69" cy="45" rx="2" ry="2.3"/><path d="M44 56 Q47 61 51 62"/><path d="M76 56 Q73 61 69 62"/>`
  if (rank >= 4) s += `<ellipse cx="51" cy="45" rx="3.2" ry="3.6" fill="${fill}" stroke="none"/><ellipse cx="69" cy="45" rx="3.2" ry="3.6" fill="${fill}" stroke="none"/><path d="M50 28 Q60 24 70 28"/><path d="M60 22 V34"/><path d="M40 46 Q42 40 47 38"/><path d="M80 46 Q78 40 73 38"/>`
  return s
}

const GLYPHS: Record<RepFaction, (rank: number, fill: string) => string> = {
  guilde: guildeGlyph, marine: marineGlyph, corsaire: corsaireGlyph, pirate: pirateGlyph,
}

export interface ReputationIconProps {
  faction: RepFaction | 'neutral'
  /**
   * Backend notoriety tier (Reputation::rank(), 0..5): 0 = neutral (Sans pavillon),
   * 1 Novice … 5 Légende. Mapped internally to the 0..4 earned art index, so raw
   * API ranks can be fed directly. Clamped. Ignored when faction is "neutral".
   */
  rank?: number
  /** Rendered px size (square). Default 120. */
  size?: number
  className?: string
  style?: CSSProperties
  title?: string
}

export function ReputationIcon({
  faction, rank = 0, size = 120, className, style, title,
}: ReputationIconProps) {
  const uid = useId().replace(/:/g, '')
  const common = {
    viewBox: '0 0 120 120', width: size, height: size, fill: 'none',
    strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
    className, style, role: title ? 'img' : undefined,
    'aria-label': title, 'aria-hidden': title ? undefined : true,
  }

  if (faction === 'neutral') {
    const c = PALETTE.gold.deep
    return (
      <svg {...common} dangerouslySetInnerHTML={{ __html:
        `<g stroke="${c}" stroke-width="2.9" opacity="0.65"><path d="M44 30 V92"/><path d="M44 32 H82 Q86 44 82 56 H44 Z"/></g>` +
        `<path d="M44 32 H82 Q86 44 82 56 H44 Z" fill="#F5EDD7" fill-opacity="0.05" stroke="none"/>` }}
      />
    )
  }

  // Backend tier (0..5; 0 = neutral, handled above) → 0..4 earned art index.
  const r = Math.max(0, Math.min(4, rank - 1))
  const legend = r >= 4
  const fill = r >= 3 ? PALETTE[faction].hi : PALETTE.gold.line
  const stroke = legend ? `url(#${uid})` : glyphColor(faction, r)

  const defs = legend
    ? `<defs><linearGradient id="${uid}" gradientUnits="userSpaceOnUse" x1="60" y1="14" x2="60" y2="106">` +
      `<stop offset="0%" stop-color="${PALETTE[faction].hi}"/>` +
      `<stop offset="100%" stop-color="${PALETTE[faction].line}"/></linearGradient></defs>`
    : ''

  // Légende: crisp everywhere — no blur. Premium from the gilded gradient + frame.
  const inner =
    defs +
    `<g>` +
    frame(r, faction) +
    rankStars(r, faction) +
    `</g>` +
    `<g stroke="${stroke}" stroke-width="${legend ? glyphSW(r) + 0.4 : glyphSW(r)}" opacity="${glyphOpacity(r)}">${GLYPHS[faction](r, fill)}</g>`

  return <svg {...common} dangerouslySetInnerHTML={{ __html: inner }} />
}

export default ReputationIcon
